/**
 * This file will manage the player versions that need to be instantiated for different videos
 * It will download the latest version inside that major version, instantiate it and save it in the playerVersions object for future use if needed
 */

import { getENV } from "../config";
import { Player } from "@blings/blings-player";
import { ISdkParams } from "@blings/blings-player/lib/src/SDK/sdk.api";
import { IBlingsPlayer } from "@blings/blings-player/lib/src/SDK";

// Define types

type PlayerVersion = {
  majorVersion: number;
  miniSDK?: MiniSDKData;
};

type MiniSDKData = {
  miniSDKDownloadedCode: string;
  blingsPlayer: IBlingsPlayer;
};

// CreatePlayerConfig now only cares about major version
type CreatePlayerConfig = {
  /**
   * The major version of the player. Ex: 5
   */
  playerMajorVersion?: number | null;
  /**
   * Should the playerManager create a new player or replace the current player
   */
  newPlayer?: boolean;
  /**
   * Use the already installed module's BlingsPlayer instead of downloading a new one.
   */
  playerFromInstalledModule?: boolean;
};

declare global {
  interface Window {
    BlingsPlayer?: IBlingsPlayer;
    define: any;
  }
}
const LATEST_MAJOR_VERSION = 4;
export default class PlayerManager {
  private static _singleton: PlayerManager;
  // Mapping by major version remains for caching the SDK.
  private playerVersions: { [major: number]: PlayerVersion } = {};

  // New properties to store the running player and its major version.
  private currentPlayer: Player | null = null;
  private runningMajorVersion: number | null = null;

  private miniSDKSource =
    getENV() === "dev"
      ? "https://assets.blings.io/scripts/msdkDev"
      : "https://assets.blings.io/scripts/msdk";

  private isPlayerOperationInProgress: boolean = false;
  private activeRequestId: number = 0;
  private mutex: Promise<void> = Promise.resolve();
  // Add a specific mutex for loadSDK operations
  private loadSDKMutex: Promise<void> = Promise.resolve();
  // Add a map to track ongoing SDK loads by major version
  private ongoingSDKLoads: Map<number, Promise<MiniSDKData>> = new Map();

  private currentDownloadController: AbortController | null = null;
  private currentInstantiateController: AbortController | null = null;

  // Existing module-installed BlingsPlayer.
  private blingsPlayerFromModule: IBlingsPlayer;

  private constructor() {
    PlayerManager._singleton = this;
    this.blingsPlayerFromModule = window["BlingsPlayer"] as IBlingsPlayer;
    delete window["BlingsPlayer"];
  }

  public static get(): PlayerManager {
    if (!PlayerManager._singleton) {
      PlayerManager._singleton = new PlayerManager();
    }
    return PlayerManager._singleton;
  }

  /**
   * Get the version of the currently running player.
   */
  public GetCurrentPlayerVersion(): string {
    if (this.currentPlayer) {
      return (
        this.currentPlayer.blingsVersion?.replaceAll(".", "-") ??
        "WARN_NO_PLAYER_VERSION"
      );
    }
    return "WARN_NO_PLAYER_VERSION";
  }

  /**
   * Get the major version number of the currently running player.
   */
  public GetCurrentMajorVersion(): string {
    return this.runningMajorVersion !== null
      ? this.runningMajorVersion.toString()
      : "NO_ACTIVE_PLAYER";
  }

  /**
   * Download the latest version of the miniSDK for a given major version
   * @param major The major version of the player that we want to download
   */
  private async downloadMiniSDK(
    major: number,
    signal: AbortSignal
  ): Promise<string> {
    let url: string;
    url = this.miniSDKSource + major + ".js";
    const response = await fetch(url, { signal });
    const code = await response.text();
    return code;
  }

  /**
   * Instantiate the minisdk and return the BlingsPlayer object that we can use to create a player
   * @param miniSDKCode The code of the minisdk that we want to instantiate
   * @returns Returns the BlingsPlayer object that we can use to create a player
   */
  private async instantiateMiniSDK(
    miniSDKCode: string,
    signal: AbortSignal
  ): Promise<IBlingsPlayer> {
    let originalDefine;
    let script: HTMLScriptElement | null = null;
    let blobUrl: string | null = null;

    try {
      originalDefine = this.unsetAMD();

      // Create a blob URL from the miniSDK code
      const blob = new Blob([miniSDKCode], { type: "application/javascript" });
      blobUrl = URL.createObjectURL(blob);

      // Create and append the script with the blob URL
      script = document.createElement("script");
      const scriptPromise = new Promise<void>((resolve, reject) => {
        script!.onload = () => resolve();
        script!.onerror = (e) => {
          console.error("Script load error:", e);
          reject(new Error("Failed to load BlingsPlayer script"));
        };
        script!.src = blobUrl!;
        document.head.appendChild(script!);
      });

      await scriptPromise;

      while (!window["BlingsPlayer"]) {
        if (signal.aborted) throw new Error("Instantiation aborted");
        await this.delay(10);
      }
      while (!window["BlingsPlayer"].version) {
        if (signal.aborted) throw new Error("Instantiation aborted");
        await this.delay(10);
      }

      if (!window["BlingsPlayer"]) {
        throw new Error(
          "BlingsPlayer was not instantiated within the expected timeframe."
        );
      }
      const blingsPlayer = window["BlingsPlayer"];
      delete window["BlingsPlayer"];
      return blingsPlayer as IBlingsPlayer;
    } catch (e) {
      console.error("ERROR ON MINISDK INSTANTIATION", e);
      throw e;
    } finally {
      // Clean up the script element
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }

      // Clean up the blob URL
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }

      this.setAMD(originalDefine);
    }
  }

  /**
   * Function to unset the AMD define function. Needed to allow the player instantiated through the minisdk to work correctly because of Webpack load mode definitions
   * @returns Returns the original state of the AMD define function
   */
  private unsetAMD() {
    const originalState = window.define;
    window.define = undefined;
    return originalState;
  }

  /**
   * Function to set the AMD define function. Needed to allow the player instantiated through the minisdk to work correctly because of Webpack load mode definitions
   * @param originalState The original state of the AMD define function
   */
  private setAMD(originalState: any) {
    window.define = originalState;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Delete any existing player by destroying it and clearing currentPlayer and runningMajorVersion.
   */
  private deleteBlingsPlayerReferences() {
    const cssRuleToRemove = [
      ".__Blings__player__container .__blings__ctrl-container.__blings__with-timeline, .__Blings__player__container .__blings__ctrl-container.__blings__stories-mode",
      ".__Blings__player__container",
      "__Blings__player",
      "BlingsPlayer",
      "__blings__ctrl-container",
      ".onLoadCover",
      ".__blings__animation-container",
      "__blings-fonts-style",
    ];
    const cssIdsToRemove = [
      "__blings__fullscreenspecificStyle",
      "version3-workaround-styles",
    ];

    // Safely destroy the current player
    if (this.currentPlayer) {
      try {
        this.currentPlayer.destroy();
      } catch (e) {
        console.error("Error destroying current player", e);
      }
      this.currentPlayer = null;
      this.runningMajorVersion = null;
    }

    // Delete the BlingsPlayer ids from the head
    for (let i = 0; i < cssIdsToRemove.length; i++) {
      const id = cssIdsToRemove[i];
      const element = document.getElementById(id);
      if (element) element.remove();
    }

    // We need to iterate backwards because when we delete a rule, the indexes of the other rules will change
    for (let j = 0; j < document.styleSheets.length; j++) {
      try {
        const cssRules = document.styleSheets[j].cssRules;
        for (let i = 0; i < cssRules.length; i++) {
          const cssRule = cssRules[i];
          if (cssRuleToRemove.includes(cssRule.cssText)) {
            document.styleSheets[j].deleteRule(i);
            i--;
          }
        }
      } catch (e) {
        //console.log("Error removing css rule", e);
        continue;
      }
    }

    // Additional cleanup: Remove workaround styles if present
    const workaroundStyles = document.getElementById(
      "version3-workaround-styles"
    );
    if (workaroundStyles) {
      workaroundStyles.remove();
    }
  }

  public deletePlayer() {
    this.deleteBlingsPlayerReferences();
  }

  private Version3Workaround() {
    // On this element, there is a rule ".__Blings__player__container .__blings__ctrl-container" that has the opacity set to 0.00001
    // We need to disable this rule so that the timeline is shown correctly
    // To do this, we need to create a new style sheet and add the rule to it

    // Check if the workaround styles are already applied
    if (document.getElementById("version3-workaround-styles")) {
      return; // Styles already applied
    }

    const newStyle = document.createElement("style");
    newStyle.id = "version3-workaround-styles"; // Assign an ID for easy reference
    newStyle.innerHTML =
      ".__Blings__player__container .__blings__ctrl-container { opacity: 1 !important; }" +
      ".onLoadCover { z-index: 120 !important; }" +
      ".__blings__animation-container { z-index: 100 !important; }" +
      ".__blings__ctrl-container { z-index: 110 !important; }";

    document.head.appendChild(newStyle);
  }

  /**
   * NEW FUNCTION: loadSDK - downloads, instantiates, caches and cleans up the SDK for a given major version.
   */
  private async loadSDK(major: number): Promise<MiniSDKData> {
    // Optimization: Check if already loaded *before* acquiring the mutex.
    // This is safe because playerVersions is only written *after* successful load.
    if (this.playerVersions[major]?.miniSDK) {
      return this.playerVersions[major]!.miniSDK!;
    }

    // Use a function scope to capture the current state for the mutex chain
    const loadAndCacheSDK = async (): Promise<MiniSDKData> => {
      // Critical Section Start: Everything from here needs to be atomic for a given 'major'
      // Check 1: Re-check if already loaded (might have happened while waiting for mutex)
      if (this.playerVersions[major]?.miniSDK) {
        return this.playerVersions[major]!.miniSDK!;
      }

      // Check 2: Re-check if another operation *started* loading this version while waiting.
      // If an ongoing load exists *now*, return its promise.
      if (this.ongoingSDKLoads.has(major)) {
        // Important: We return the existing promise from the map.
        // The caller will await the *original* operation started by another thread.
        return this.ongoingSDKLoads.get(major)!;
      }

      // If we reach here, we are the *first* operation for this 'major' version
      // to acquire the mutex *and* find no completed or ongoing load.
      // We must now perform the load.

      // Create the actual load promise *inside* the mutex-protected section
      const actualLoadPromise = (async () => {
        // Create an internal AbortController for this specific load attempt.
        const sdkLoadController = new AbortController();
        const signal = sdkLoadController.signal;
        try {
          // Pass the internal signal
          const miniSDKCode = await this.downloadMiniSDK(major, signal);

          // Check if the signal was aborted *during* download (e.g., network error causing abort)
          // Although less likely to be aborted externally now, it's good practice.
          if (signal.aborted)
            throw new Error(
              `SDK download aborted internally for major ${major}`
            );

          // Pass the internal signal
          const instantiatedMiniSDKCreator = await this.instantiateMiniSDK(
            miniSDKCode,
            signal
          );
          const sdkData: MiniSDKData = {
            miniSDKDownloadedCode: miniSDKCode,
            blingsPlayer: instantiatedMiniSDKCreator,
          };

          // Cache the successfully loaded SDK data
          this.playerVersions[major] = {
            majorVersion: major,
            miniSDK: sdkData,
          };
          return sdkData;
        } catch (error) {
          // Rethrow to ensure the promise rejects correctly
          throw error;
        } finally {
          // Critical: Always remove from ongoing loads *after* completion or failure,
          // ensuring subsequent calls don't perpetually wait on a failed/aborted promise.
          this.ongoingSDKLoads.delete(major);
        }
      })();

      // Store the promise for *this specific load attempt* in the map immediately.
      this.ongoingSDKLoads.set(major, actualLoadPromise);

      return actualLoadPromise;
    };

    // Chain the operation onto the mutex.
    // The actual logic (loadAndCacheSDK) only runs when the mutex is acquired.
    const loadOperationPromise = this.loadSDKMutex.then(loadAndCacheSDK);

    // Update the mutex to sequence the *next* call after this one completes or fails.
    // We attach a catch to prevent unhandled promise rejections from breaking the mutex chain.
    this.loadSDKMutex = loadOperationPromise.then(() => {}).catch(() => {});

    // Return the promise that resolves with the SDK data (or rejects on error).
    return loadOperationPromise;
  }

  /**
   * UPDATED createPlayer function.
   * Creates a player for a given major version.
   * Only one player exists at a time; its instance and majorVersion are stored in PlayerManager.
   */
  public async createPlayer(
    playerParams: ISdkParams,
    config: CreatePlayerConfig
  ): Promise<Player> {
    // Generate a new request ID for this call
    const currentRequestId = ++this.activeRequestId;

    // Use mutex to ensure sequential execution
    const playerPromise = this.mutex.then(async () => {
      // --- CRITICAL SECTION START ---
      // Check if this request has been superseded *before* doing anything else.
      if (currentRequestId !== this.activeRequestId) {
        throw new Error("Request superseded by a newer request");
      }

      // If we are here, this is the currently active request.
      // We no longer call cancelOngoingProcesses() here as SDK loading is independent.

      // Set the flag to indicate an operation is in progress *for this active request*.
      // We do this early so subsequent checks know *something* is happening.
      this.isPlayerOperationInProgress = true;

      // We no longer need specific download/instantiate controllers/signals here.
      // --- CRITICAL SECTION END (Setup Phase) ---

      try {
        // Handle module-installed player case first (simpler, less async)
        if (config.playerFromInstalledModule && this.blingsPlayerFromModule) {
          // Clean up any previous *downloaded* player just in case.
          this.deleteBlingsPlayerReferences();
          this.currentPlayer = await this.blingsPlayerFromModule.create(
            playerParams
          );
          this.runningMajorVersion = -1; // Denote module version with -1.
          return this.currentPlayer;
        }

        // Determine the major version and initiate SDK loading
        const major = config.playerMajorVersion ?? LATEST_MAJOR_VERSION;

        // Call loadSDK (without signals).
        const sdkData = await this.loadSDK(major);

        // --- Player Creation Phase ---
        // Check *again* if this is still the active request *after* SDK load.
        if (currentRequestId !== this.activeRequestId) {
          // console.warn(
          //   `Request ${currentRequestId} superseded by ${this.activeRequestId} after SDK load, aborting creation.`
          // );
          throw new Error(
            "Request superseded by a newer request after SDK load"
          );
        }

        // *** CRITICAL CHANGE: Clean up *just before* creating the new player ***
        this.deleteBlingsPlayerReferences();

        // If we are still the active request, proceed to create the player.
        this.currentPlayer = await sdkData.blingsPlayer.create(playerParams);
        this.runningMajorVersion = major;

        // Apply Version3Workaround if needed (assuming it should apply to the newly created player)
        if (major === 3) {
          this.Version3Workaround();
        }

        return this.currentPlayer;
      } catch (error: any) {
        // Log specific error types
        if (error.name === "AbortError") {
          // console.warn(
          //   `Request ${currentRequestId} encountered AbortError:`,
          //   error.message
          // );
          // Propagate AbortError
          throw error;
        } else if (error.message?.includes("superseded")) {
          // console.warn(
          //   `Request ${currentRequestId} explicitly aborted due to superseding.`
          // );
          // *** CHANGE: Do not re-throw superseded errors. ***
          // Let the newer request proceed. The promise associated with *this*
          // request will just hang (or resolve with undefined), which should be handled
          // by the caller if necessary, but typically isn't needed as a newer call exists.
        } else {
          // console.error(
          //   `Request ${currentRequestId} encountered error during player creation:`,
          //   error
          // );
          // Rethrow other unexpected errors
          throw error;
        }
      } finally {
        // Only clear the flag if this *specific* request finished (successfully or with error)
        // AND it is *still* the active one. Avoids clearing the flag if superseded.
        if (currentRequestId === this.activeRequestId) {
          this.isPlayerOperationInProgress = false;
        }
        // --- End of Active Request Specific Logic ---
      }
    });

    // Update the mutex to chain after the current operation, regardless of success or failure
    this.mutex = playerPromise.then(() => {}).catch(() => {}); // Prevent unhandled rejections breaking the chain

    // Return the promise. It will resolve with the Player if successful and active,
    // or reject if superseded or another error occurred.
    return playerPromise as Promise<Player>;
  }
}
